// [AI generated. Not yet reviewed by human.]
// Follows the Pascall login page UI standard (login-page-ui-standard.md).
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAtlassian } from "@fortawesome/free-brands-svg-icons";
import { faChartLine, faClipboardCheck, faGraduationCap, faLock } from "@fortawesome/free-solid-svg-icons";
import { ATLASSIAN_LOGIN_API, MICROSOFT_LOGIN_API } from "./constants";
import classes from "./styles.module.css";
import { SESSION_ENDED_REASON } from "../../utils/sessionEnded";

// ---- Per-app settings (see section 3 of the standard) ----
const BRAND_NAME = "Pascall Systems";
const HEADLINE = "Track every quality KPI in one place.";
const SUBTITLE = "Live quality metrics from Jira and Confluence, all on one dashboard.";
const CARD_SUBTITLE = "Sign in to Pascall KPI";
const ACCESS_NOTICE = "Access is limited to authorized Pascall users.";
const HELP_TEXT = "Trouble signing in? Contact your IT administrator.";
const SESSION_ENDED_NOTICE = "An administrator ended your session. Sign in again if you still need access.";

const FEATURES = [
    { icon: faChartLine, title: "Live dashboards", description: "See every QMS metric at a glance." },
    { icon: faClipboardCheck, title: "CAPA & nonconformance", description: "Follow open, resolved, and overdue items." },
    { icon: faGraduationCap, title: "Training compliance", description: "Monitor training completion across teams." },
];
// ---------------------------------------------------------

// Microsoft's four-square mark, in color, because that's what users recognize.
const MicrosoftLogo = () => (
    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
);

type Provider = { id: string; label: string; url: string; logo: JSX.Element };

// Keep Microsoft first. Remove any provider this app doesn't support.
const PROVIDERS: Provider[] = [
    { id: "microsoft", label: "Continue with Microsoft", url: MICROSOFT_LOGIN_API, logo: <MicrosoftLogo /> },
    {
        id: "atlassian",
        label: "Continue with Atlassian",
        url: ATLASSIAN_LOGIN_API,
        logo: <FontAwesomeIcon icon={faAtlassian} className={classes.atlassianIcon} />,
    },
];

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
        return "Good morning";
    }
    if (hour < 18) {
        return "Good afternoon";
    }
    return "Good evening";
};

const BrandPanel = () => (
    <aside className={classes.brandPanel}>
        <div className={classes.brandHeader}>
            <div className={classes.brandMark}>P</div>
            <span className={classes.brandName}>{BRAND_NAME}</span>
        </div>
        <div className={classes.brandBody}>
            <h1 className={classes.brandTitle}>{HEADLINE}</h1>
            <p className={classes.brandSubtitle}>{SUBTITLE}</p>
            <ul className={classes.featureList}>
                {FEATURES.map((feature) => (
                    <li key={feature.title} className={classes.featureItem}>
                        <span className={classes.featureIcon}>
                            <FontAwesomeIcon icon={feature.icon} />
                        </span>
                        <div>
                            <div className={classes.featureTitle}>{feature.title}</div>
                            <div className={classes.featureDescription}>{feature.description}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
        <div className={classes.brandFooter}>© {new Date().getFullYear()} Pascall Systems</div>
    </aside>
);

const SignInPage = () => {
    const [redirectingTo, setRedirectingTo] = useState<string | null>(null);

    const onSignIn = (provider: Provider) => {
        setRedirectingTo(provider.id);
        window.location.href = provider.url;
    };

    return (
        <div className={classes.page}>
            <BrandPanel />
            <main className={classes.formPanel}>
                <div className={classes.card}>
                    <div className={`${classes.brandMark} ${classes.cardMark}`}>P</div>
                    <h2 className={classes.cardTitle}>{getGreeting()} 👋</h2>
                    <p className={classes.cardSubtitle}>{CARD_SUBTITLE}</p>

                    {SESSION_ENDED_REASON === "revoked" && (
                        <div className={classes.sessionEndedNotice} role="status">
                            {SESSION_ENDED_NOTICE}
                        </div>
                    )}

                    <div className={classes.providerList}>
                        {PROVIDERS.map((provider) => (
                            <button
                                key={provider.id}
                                type="button"
                                className={classes.providerButton}
                                disabled={!!redirectingTo}
                                onClick={() => onSignIn(provider)}
                            >
                                <span className={classes.providerLogo}>
                                    {redirectingTo === provider.id ? (
                                        <span className={classes.spinner} aria-hidden="true" />
                                    ) : (
                                        provider.logo
                                    )}
                                </span>
                                <span>{redirectingTo === provider.id ? "Redirecting…" : provider.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className={classes.notice}>
                        <FontAwesomeIcon icon={faLock} className={classes.noticeIcon} />
                        {ACCESS_NOTICE}
                    </div>
                </div>
                <p className={classes.helpText}>{HELP_TEXT}</p>
            </main>
        </div>
    );
};

export default SignInPage;
